import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, collection, getDocs } from '../../firebase/config';
import { Scissors, Sparkles, Droplets, Clock, Heart, ArrowRight } from 'lucide-react';

const DiscoverCategoriesPage = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([
    { id: 'hair', name: 'Hair', icon: <Scissors size={32} />, count: 0, img: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=400&q=80' },
    { id: 'nails', name: 'Nails', icon: <Sparkles size={32} />, count: 0, img: 'https://images.unsplash.com/photo-1604654894610-df63bc536371?auto=format&fit=crop&w=400&q=80' },
    { id: 'skin', name: 'Skin', icon: <Droplets size={32} />, count: 0, img: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?auto=format&fit=crop&w=400&q=80' },
    { id: 'lashes', name: 'Lashes', icon: <Clock size={32} />, count: 0, img: 'https://images.unsplash.com/photo-1512496015851-a1c8f35dd9a2?auto=format&fit=crop&w=400&q=80' },
    { id: 'spa', name: 'Spa', icon: <Heart size={32} />, count: 0, img: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=400&q=80' },
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCounts = async () => {
      try {
        // Load services (not users!) — each service has a `specialization` field
        const snapshot = await getDocs(collection(db, 'services'));
        const servicesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Count services per category
        const updated = categories.map(cat => {
          const count = servicesData.filter(service => {
            const spec = (service.specialization || '').toLowerCase();
            const catId = cat.id;

            if (catId === 'hair') return spec.includes('hair');
            if (catId === 'nails') return spec.includes('nail');
            if (catId === 'skin') return spec.includes('skin') || spec.includes('esthetician');
            if (catId === 'lashes') return spec.includes('lash');
            if (catId === 'spa') return spec.includes('spa') || spec.includes('massage');

            return spec.includes(catId);
          }).length;

          return { ...cat, count };
        });

        setCategories(updated);
      } catch (error) {
        console.error('Error loading category counts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadCounts();
  }, []);

  const handleCategoryClick = (categoryId) => {
    navigate(`/dashboard/discover/${categoryId}`);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold text-gray-900">Discover</h1>
        <p className="text-gray-500 mt-1">Browse services by category</p>
      </div>

      {/* Categories List */}
      <div className="space-y-4">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => handleCategoryClick(cat.id)}
            className="w-full bg-white rounded-[2rem] shadow-sm border border-gray-50 hover:shadow-md transition-all overflow-hidden group flex items-center"
          >
            {/* Image */}
            <div className="relative w-32 h-32 md:w-40 md:h-32 shrink-0 overflow-hidden">
              <img
                src={cat.img}
                alt={cat.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            </div>

            {/* Content */}
            <div className="flex-1 p-4 md:p-6 text-left">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6]">
                  {cat.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-serif font-bold text-gray-900">
                  {cat.name}
                </h3>
              </div>
              <p className="text-sm text-gray-500 font-medium">
                {loading ? 'Loading...' : `${cat.count} Service${cat.count === 1 ? '' : 's'}`}
              </p>
            </div>

            {/* Arrow */}
            <div className="pr-6 text-[#EFA9B6] group-hover:translate-x-1 transition-transform">
              <ArrowRight size={24} />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default DiscoverCategoriesPage;